import styled, { css } from 'styled-components';

import {
  useTerminalDrawerActions,
  useTerminalDrawerState,
} from '#/features/terminal/drawer-context';

import BlockTradeContextPump from '#/components/Terminal/BlockTradeContextPump';
import ChatView from '#/components/Terminal/ChatView';
import CronView from '#/components/Terminal/CronView';
import ExamplesView from '#/components/Terminal/ExamplesView';
import PendingMessageDrain from '#/components/Terminal/PendingMessageDrain';

export default function BlockTerminalTab() {
  return (
    <Container>
      <BlockTradeContextPump />
      <PendingMessageDrain />
      <Header />
      <Body />
    </Container>
  );
}

function Header() {
  const drawerState = useTerminalDrawerState();
  const drawerActions = useTerminalDrawerActions();
  return (
    <HeaderRow>
      <TabStrip>
        <TabButton
          $active={drawerState.activeTab === 'chat'}
          onClick={() => drawerActions.setActiveTab('chat')}
          type="button"
        >
          Chat
        </TabButton>
        <TabButton
          $active={drawerState.activeTab === 'examples'}
          onClick={() => drawerActions.setActiveTab('examples')}
          type="button"
        >
          Examples
        </TabButton>
        <TabButton
          $active={drawerState.activeTab === 'cron'}
          onClick={() => drawerActions.setActiveTab('cron')}
          type="button"
        >
          Jobs
        </TabButton>
      </TabStrip>
      <PinSegment>
        <PinSegmentButton
          $active={drawerState.pinMode === 'bottom-tab'}
          onClick={() => drawerActions.setPinMode('bottom-tab')}
          type="button"
          title="Pin to bottom tab"
        >
          Bottom
        </PinSegmentButton>
        <PinSegmentButton
          $active={drawerState.pinMode === 'side-panel'}
          onClick={() => {
            drawerActions.setPinMode('side-panel');
            // Ensure drawer stays open when switching to side-panel
            if (!drawerState.isOpen) {
              drawerActions.open();
            }
          }}
          type="button"
          title="Pin as side panel"
        >
          Side
        </PinSegmentButton>
      </PinSegment>
    </HeaderRow>
  );
}

function Body() {
  const { activeTab } = useTerminalDrawerState();
  return (
    <BodyContainer>
      <TabPane $active={activeTab === 'chat'}>
        <ChatView />
      </TabPane>
      <TabPane $active={activeTab === 'examples'}>
        <ExamplesView />
      </TabPane>
      <TabPane $active={activeTab === 'cron'}>
        <CronView />
      </TabPane>
    </BodyContainer>
  );
}

const Container = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 8px;
  border-bottom: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
`;

const TabStrip = styled.div`
  display: flex;
  gap: 4px;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;

  ${(p) =>
    p.$active &&
    css`
      background: var(--hover-bg, rgba(255, 255, 255, 0.08));
      color: var(--text-primary);
      border-color: var(--border-color, rgba(255, 255, 255, 0.12));
    `}

  &:hover {
    color: var(--text-primary);
  }
`;

const PinSegment = styled.div`
  display: flex;
  border: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
  border-radius: 6px;
  overflow: hidden;
`;

const PinSegmentButton = styled.button<{ $active: boolean }>`
  padding: 3px 10px;
  font-size: 0.6875rem;
  font-weight: 500;
  background: ${(p) =>
    p.$active ? 'var(--hover-bg, rgba(255, 255, 255, 0.08))' : 'transparent'};
  color: ${(p) =>
    p.$active ? 'var(--text-primary)' : 'var(--text-secondary)'};
  border: none;
  cursor: pointer;
  transition: background 0.15s ease;

  & + & {
    border-left: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
  }

  &:hover {
    color: var(--text-primary);
  }
`;

const BodyContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const TabPane = styled.div<{ $active: boolean }>`
  display: ${(p) => (p.$active ? 'flex' : 'none')};
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;

// Re-export drawer types so other components can flag-gate without an
// extra import.
export type { PinMode, TerminalTab } from '#/features/terminal/drawer-context';
